import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

const NAV = [
  { to: '/', label: 'Início', end: true },
  { to: '/aulas', label: 'Aulas', end: false },
  { to: '/aulas/1', label: 'Conteúdos', end: false },
  { to: '/teste-de-nivel', label: 'Teste de nível', end: false },
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const firstName = user?.name.split(' ')[0] ?? '';
  const isAdmin = user?.accessLevel === 'admin';

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-6 text-[13px] font-bold transition ${
      isActive ? 'text-[#ff5a17]' : 'text-[#252a31] hover:text-[#ff5a17]'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-[#f0e8e2] bg-white/96 backdrop-blur-xl">
      <div className="mx-auto flex h-[58px] max-w-[1440px] items-center justify-between px-3 sm:h-[64px] sm:px-5 lg:h-[72px] lg:px-10">
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center text-[#ff9700] sm:h-10 sm:w-10 lg:h-11 lg:w-11">
            <i className="ri-music-2-fill text-[31px] sm:text-[34px] lg:text-[39px]" />
          </span>
          <div className="min-w-0 leading-none">
            <span className="block truncate text-[19px] font-black tracking-[-0.03em] text-[#191919] sm:text-[22px] lg:text-[25px]">
              PARTITURAS
            </span>
            <span className="mt-1 hidden truncate text-[8px] font-extrabold tracking-[0.19em] text-[#737780] sm:block lg:text-[9px]">
              MÚSICA AO SEU ALCANCE
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <NavLink key={item.label} to={item.to} end={item.end} className={navClass}>
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive ? (
                    <span className="absolute inset-x-3 bottom-[15px] h-[2px] rounded-full bg-[#ff5a17]" />
                  ) : null}
                </>
              )}
            </NavLink>
          ))}
          <a
            href="/#ajuda"
            className="px-3 py-6 text-[13px] font-bold text-[#252a31] transition hover:text-[#ff5a17]"
          >
            Ajuda
          </a>
        </nav>

        <div className="hidden items-center gap-2.5 lg:flex">
          <Link
            to="/aulas"
            aria-label="Abrir conteúdos"
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#20242a] transition hover:bg-orange-50 hover:text-[#ff5a17]"
          >
            <i className="ri-search-line text-[22px]" />
          </Link>

          {user ? (
            <>
              <Link
                to="/aulas"
                className="inline-flex items-center gap-2 rounded-full px-3 py-2.5 text-[13px] font-bold text-[#252a31] hover:bg-orange-50"
              >
                <i className="ri-user-3-fill text-lg" />
                {firstName}
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  aria-label="Abrir painel administrativo"
                  className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-2 text-[12px] font-extrabold text-orange-700 hover:bg-orange-100"
                >
                  <i className="ri-shield-user-line text-lg" />
                  Administração
                </Link>
              )}
              <button
                type="button"
                onClick={() => void logout()}
                className="rounded-full px-3 py-2.5 text-[12px] font-bold text-[#737780] hover:text-[#ff5a17]"
              >
                Sair
              </button>
            </>
          ) : (
            <>
              <Link
                to="/entrar"
                className="inline-flex items-center gap-2 rounded-full px-3 py-2.5 text-[13px] font-bold text-[#252a31] hover:text-[#ff5a17]"
              >
                <i className="ri-user-3-fill text-lg" />
                Entrar
              </Link>
              <Link
                to="/criar-conta?next=%2Faulas%2F1"
                className="rounded-full bg-[#ff5a17] px-5 py-3 text-[13px] font-extrabold text-white shadow-[0_8px_20px_rgba(255,90,23,0.22)] transition hover:bg-[#ed4a0b]"
              >
                Criar conta
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label="Abrir menu"
          onClick={() => setOpen((value) => !value)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-[#252a31] hover:bg-orange-50 lg:hidden"
        >
          <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
        </button>
      </div>

      {open ? (
        <div className="border-t border-[#f0e8e2] bg-white px-4 py-3 lg:hidden">
          <div className="mx-auto flex max-w-[1440px] flex-col">
            {NAV.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setOpen(false)}
                className="rounded-xl px-3 py-3 text-sm font-bold text-[#252a31] hover:bg-orange-50 hover:text-[#ff5a17]"
              >
                {item.label}
              </Link>
            ))}
            <a
              href="/#ajuda"
              onClick={() => setOpen(false)}
              className="rounded-xl px-3 py-3 text-sm font-bold text-[#252a31] hover:bg-orange-50"
            >
              Ajuda
            </a>

            {user ? (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#fff6ef] px-4 py-3">
                <span className="text-sm font-extrabold text-[#5c2c13]">{firstName}</span>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    void logout();
                  }}
                  className="text-sm font-bold text-[#69707a]"
                >
                  Sair
                </button>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-sm font-extrabold text-white hover:bg-orange-600"
                  >
                    <i className="ri-shield-user-line text-lg" />
                    Administração
                  </Link>
                )}
              </div>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Link
                  to="/entrar"
                  onClick={() => setOpen(false)}
                  className="rounded-full border border-[#ffc8aa] px-4 py-3 text-center text-sm font-bold text-[#e65312]"
                >
                  Entrar
                </Link>
                <Link
                  to="/criar-conta?next=%2Faulas%2F1"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-[#ff5a17] px-4 py-3 text-center text-sm font-extrabold text-white"
                >
                  Criar conta
                </Link>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}
